import React, { useMemo, useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	Modal,
	Radio,
	TextField,
	Typography,
	Alert,
	Switch,
} from "@mui/material";
import { FormNameDesc } from "../Simulation/FormNameDesc";
import { useTranslation } from "react-i18next";

const ModalFecha = ({ open, onClose, onExecuteSimulation }) => {
	const { t } = useTranslation();

	const [fechaFin, setFechaFin] = useState("");
	const [fechaInicio, setFechaInicio] = useState("");
	const [fechaError, setFechaError] = useState("");
	const [checkedError, setCheckedError] = useState("");
	const [checked, setChecked] = useState("");

	const [formValues, setFormValues] = useState({ name: "", description: "" });
	const [formErrors, setFormErrors] = useState({ name: false, description: false });
	const [showAlert, setShowAlert] = useState(false);
	const [emailNotifications, setEmailNotifications] = useState(false);

	const handleFormChange = (name, value) => {
		setFormValues((p) => ({ ...p, [name]: value }));
		setFormErrors((p) => ({ ...p, [name]: false }));
	};

	const areDatesValid = useMemo(() => {
		if (!(fechaInicio && fechaFin)) return false;
		const [yi, mi] = fechaInicio.split("-");
		const [yf, mf] = fechaFin.split("-");
		const iniTotal = Number(yi) * 12 + Number(mi);
		const finTotal = Number(yf) * 12 + Number(mf);
		return iniTotal <= finTotal;
	}, [fechaInicio, fechaFin]);

	const canExecute =
		!!checked &&
		areDatesValid &&
		formValues.name.trim() !== "" &&
		formValues.description.trim() !== "";

	const handleButtonClick = () => {
		const errors = {
			name: formValues.name.trim() === "",
			description: formValues.description.trim() === "",
		};
		setFormErrors(errors);

		if (!(fechaInicio && fechaFin)) {
			setFechaError(t("aux.messages.date_obligatory"));
			return;
		}
		if (!areDatesValid) {
			setFechaError(t("aux.messages.date_error"));
			return;
		}
		setFechaError("");

		if (!checked) {
			setCheckedError(t("aux.messages.select_checkbox"));
			return;
		} else {
			setCheckedError("");
		}

		if (errors.name || errors.description) {
			setShowAlert(true);
			return;
		} else {
			setShowAlert(false);
		}

		// Ejecuta SIN forecast
		onExecuteSimulation(fechaInicio, fechaFin, checked, formValues, emailNotifications);

		// reset
		setFechaFin("");
		setFechaInicio("");
		setChecked("");
		setEmailNotifications(false);
		setFormValues({ name: "", description: "" });
		onClose();
	};

	const handleRadioChange = (event) => {
		const { name } = event.target;
		setChecked(name);
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: "40%",
					backgroundColor: "background.paper",
					border: "1px",
					borderRadius: "4px",
					borderColor: "secondary.main",
					boxShadow: 30,
					p: 2,
					overflowY: "auto",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: "column",
				}}
			>
				<Typography variant="h6" gutterBottom>
					{t("aux.final_adjustments")}
				</Typography>

				<Grid container spacing={2} sx={{ width: "100%" }}>
					<Grid item xs={12}>
						<FormControl
							required
							error={!!checkedError}
							component="fieldset"
							sx={{
								width: "100%",
								mb: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							variant="standard"
						>
							<FormGroup>
								<FormLabel component="legend">{t("sim.sim_form.origen_demand")}</FormLabel>
								<FormControlLabel
									control={
										<Radio checked={checked === "KRONOS"} onChange={handleRadioChange} name="KRONOS" />
									}
									label="UKG"
								/>
								<FormControlLabel
									control={<Radio checked={checked === "DB"} onChange={handleRadioChange} name="DB" />}
									label={t("sim.sim_form.db")}
								/>
							</FormGroup>
							{checkedError && <FormHelperText>{t("aux.messages.select_checkbox")}</FormHelperText>}
						</FormControl>
					</Grid>
				</Grid>

				<Grid container spacing={2} sx={{ width: "100%" }}>
					<Grid item xs={6}>
						<TextField
							id="dateInicio"
							label={t("aux.date_start")}
							type="month"
							value={fechaInicio}
							onChange={(e) => setFechaInicio(e.target.value)}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%", m: 2 }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
							error={!!fechaError}
							helperText={fechaError && !fechaInicio ? fechaError : ""}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="dateFin"
							label={t("aux.date_end")}
							type="month"
							value={fechaFin}
							onChange={(e) => setFechaFin(e.target.value)}
							error={!!fechaError}
							helperText={
								fechaError
									? fechaError
									: fechaInicio && fechaFin && !areDatesValid
									? t("aux.messages.date_error")
									: ""
							}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%" }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
						/>
					</Grid>

					<Grid item xs={12}>
						<FormNameDesc
							formValues={formValues}
							formErrors={formErrors}
							onFormChange={handleFormChange}
						/>
					</Grid>

					{showAlert && (
						<Grid item xs={12}>
							<Alert severity="error">{t("aux.messages.error_campos")}</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={emailNotifications}
									onChange={(e) => setEmailNotifications(e.target.checked)}
								/>
							}
							label={t("sim.sim_form.email_notify")}
						/>
					</Grid>

					<Grid item xs={12}>
						<Button
							variant="contained"
							color="primary"
							onClick={handleButtonClick}
							sx={{ width: "100%" }}
							disabled={!canExecute}
						>
							{t("aux.buttons.sim.execute")}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Modal>
	);
};

export { ModalFecha };
